import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Section = styled.section`
  padding: clamp(40px, 8vw, 80px) 0;
  background: var(--bg);
`;

const Container = styled.div`
  width: min(1400px, 100vw);
  margin: 0 auto;
  padding-left: max(clamp(12px, 4vw, 18px), env(safe-area-inset-left));
  padding-right: max(clamp(12px, 4vw, 18px), env(safe-area-inset-right));
`;

const Title = styled.h2`
  text-align: center;
  margin: 0 0 clamp(24px, 5vw, 48px);
  font-size: clamp(24px, 4.6vw, 36px);
  line-height: 1.2;
`;

const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(24px, 5vw, 48px);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoBlock = styled.div`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: var(--radius);
  padding: 32px;
  box-shadow: var(--shadow);
`;

const InfoTitle = styled.h3`
  margin: 0 0 24px;
  font-size: 20px;
  color: var(--fg);
`;

const InfoItem = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 4px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--fg);
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--primary);
    }
  }
`;

const MapContainer = styled.div`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  height: 100%;
  min-height: 400px;
  position: relative;

  @media (max-width: 768px) {
    height: 300px;
    min-height: 300px;
  }
`;

const YandexMap = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: var(--radius);
`;

const OfficeHours = styled.div`
  background: var(--card);
  border: 1px solid var(--bdr);
  border-radius: var(--radius);
  padding: 24px;
  box-shadow: var(--shadow);
`;

const HoursTitle = styled.h4`
  margin: 0 0 16px;
  font-size: 18px;
  color: var(--fg);
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--bdr);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DayLabel = styled.span`
  font-weight: 500;
  color: var(--fg);
`;

const TimeLabel = styled.span`
  color: var(--muted);
  font-size: 14px;
`;

export default function ContactsSection() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ru';

  const contactData = {
    phone: '+7 (495) 972-97-97',
    email: '9@astorius.ru',
  };

  const workingHours = [
    { 
      day: t('contacts.mondayFriday'),
      time: '10:00 - 19:00' 
    },
    { 
      day: t('contacts.saturday'),
      time: '10:00 - 16:00' 
    },
    { 
      day: t('contacts.sunday'),
      time: t('contacts.closed')
    }
  ];

  // Яндекс карта для ул. Бахрушина, 23 стр. 1 (Координаты: 55.741, 37.632)
  const yandexMapSrc = "https://yandex.ru/map-widget/v1/?um=constructor%3A8c8c8c8c8c8c8c8c8c8c8c8c8c8c8c8c&amp;source=constructor";

  return (
    <Section>
      <Container>
        <Title>{t('contacts.title')}</Title>
        
        <ContactsGrid>
          <ContactInfo>
            <InfoBlock>
              <InfoTitle>{t('contacts.contactInfo')}</InfoTitle>
              
              <InfoItem>
                <InfoLabel>{t('contacts.phone')}</InfoLabel>
                <InfoValue>
                  <a href={`tel:${contactData.phone}`}>{contactData.phone}</a>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('contacts.email')}</InfoLabel>
                <InfoValue>
                  <a href={`mailto:${contactData.email}`}>{contactData.email}</a>
                </InfoValue>
              </InfoItem>

              <InfoItem>
                <InfoLabel>{t('contacts.address')}</InfoLabel>
                <InfoValue>
                  {t('contacts.addressValue')}
                </InfoValue>
              </InfoItem>
            </InfoBlock>

            <OfficeHours>
              <HoursTitle>{t('contacts.workingHours')}</HoursTitle>
              {workingHours.map((item, index) => (
                <HoursItem key={index}>
                  <DayLabel>{item.day}</DayLabel>
                  <TimeLabel>{item.time}</TimeLabel>
                </HoursItem>
              ))}
            </OfficeHours>
          </ContactInfo>

          <MapContainer>
            <YandexMap
              src="https://yandex.ru/map-widget/v1/?ll=37.632%2C55.741&z=16&l=map&pt=37.632%2C55.741,pm2rdm"
              allowFullScreen=""
              loading="lazy"
              title="Карта офиса АСТОРИУС - ул. Бахрушина, 23 стр. 1"
            />
          </MapContainer>
        </ContactsGrid>
      </Container>
    </Section>
  );
}